// LeadsNovaFloatingChat — the bottom-right AI companion.
//
// Nova is the AI Employee the operator can ask ANYTHING about
// their pipeline. Backed by /api/leads/assistant (built during
// Lead CC Phase 7 bonus round). Sits in the corner as a floating
// bubble; expands to a chat panel on click.
//
// Suggested prompts prefilled from the operator's current queue
// state so the first click gets useful output ("Summarize my
// top-3 hot leads", "Draft a text for Robert Davis").
//
// Uses the LeadIntelligenceContext to pick a live lead for its
// initial suggestions.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';
import { useLeadIntelligence } from './LeadIntelligenceContext';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function LeadsNovaFloatingChat({ leads = [] }) {
  const intel = useLeadIntelligence();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const scrollRef = React.useRef(null);

  const topLead = React.useMemo(() => {
    const insights = intel.data?.insights_by_lead_id || {};
    const hot = Object.entries(insights)
      .filter(([, v]) => v.priority_bucket === 'call_now' || v.priority_bucket === 'call_today')
      .sort((a, b) => b[1].probability_pct - a[1].probability_pct)[0];
    if (!hot) return null;
    const [id] = hot;
    return leads.find(l => l.id === id) || null;
  }, [intel.data, leads]);

  const quickPrompts = React.useMemo(() => {
    const out = [
      `What should I do first?`,
      `Summarize my top hot leads`,
    ];
    if (topLead?.name) {
      out.push(`Draft a text for ${topLead.name}`);
      out.push(`What's the next step for ${topLead.name}?`);
    }
    return out;
  }, [topLead]);

  const submit = async (question) => {
    if (!question.trim()) return;
    setBusy(true);
    const userMsg = { role: 'user', text: question };
    setMessages(m => [...m, userMsg]);
    setQ('');
    try {
      // If the question mentions a specific lead by name, target
      // that lead. Otherwise pick topLead (or leave null → general).
      const named = leads.find(l => l.name && question.toLowerCase().includes(l.name.toLowerCase()));
      const target = named || topLead || leads[0] || null;
      if (!target) {
        setMessages(m => [...m, { role: 'ai', text: 'Add a lead first, then I can help.' }]);
        setBusy(false);
        return;
      }
      const r = await fetch('/api/leads/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({ lead: target, question }),
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        const text = j.draft ? j.draft : (j.answer || j.headline || 'No answer.');
        setMessages(m => [...m, { role: 'ai', text, sources: j.source }]);
      } else {
        setMessages(m => [...m, { role: 'ai', text: `(error) ${j.error || 'HTTP ' + r.status}` }]);
      }
    } catch (err) {
      setMessages(m => [...m, { role: 'ai', text: `(error) ${err.message}` }]);
    } finally {
      setBusy(false);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 10);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          // Bottom-LEFT to avoid the crowded bottom-right corner where
          // SupportChat (green circle, right-4 z-50), AI CEO launcher
          // (purple pill, right-44 z-40), and other floating widgets
          // already live. Nova sat directly on top of SupportChat's
          // hit-area at right:24 z-1000 previously, clipping unclickable.
          position: 'fixed', bottom: 24, left: 24, zIndex: 1000,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 100%)',
          color: '#22d3ee',
          border: '1px solid #1e293b',
          borderRadius: 999,
          padding: '12px 18px',
          fontSize: 13, fontWeight: 800,
          cursor: 'pointer',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: 999, background: '#22c55e',
          animation: 'nova-pulse 1.4s ease-in-out infinite',
        }}/>
        🤖 Nova
        <style>{`
          @keyframes nova-pulse {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.4); }
          }
        `}</style>
      </button>
    );
  }

  return (
    <div style={{
      // Same bottom-LEFT anchor as the collapsed bubble so opening
      // Nova doesn't jump across the screen. Panel expands upward +
      // rightward from the bottom-left button.
      position: 'fixed', bottom: 24, left: 24, zIndex: 1000,
      width: 380, maxHeight: '70vh',
      background: '#0f172a',
      border: '1px solid #1e293b',
      borderRadius: 14,
      padding: 12,
      color: '#e2e8f0',
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 800 }}>
          🤖 Nova
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>Ask about your pipeline.</div>
        <button
          onClick={() => setOpen(false)}
          style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer' }}
          aria-label="Close"
        >×</button>
      </div>

      {messages.length === 0 && (
        <div style={{ padding: 10, fontSize: 12, color: '#cbd5e1', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, marginBottom: 10 }}>
          I've reviewed today's list.
          {intel.data?.counts?.hot > 0 && (
            <> <strong>{intel.data.counts.hot} hot leads.</strong></>
          )}
          {intel.data?.counts?.at_risk > 0 && (
            <> <strong>{intel.data.counts.at_risk} at-risk.</strong></>
          )}
          {' '}Ready to start?
        </div>
      )}

      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', display: 'grid', gap: 8,
        maxHeight: '38vh', marginBottom: 8,
      }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            background: m.role === 'user' ? '#22d3ee22' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${m.role === 'user' ? '#22d3ee44' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: 8, padding: '8px 10px',
            fontSize: 12, whiteSpace: 'pre-wrap',
            color: m.role === 'user' ? '#e0f2fe' : '#e2e8f0',
          }}>{m.text}
            {m.sources?.length > 0 && (
              <div style={{ fontSize: 9, color: '#64748b', marginTop: 4 }}>Source: {m.sources.join(', ')}</div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {quickPrompts.map(qp => (
          <button
            key={qp}
            onClick={() => submit(qp)}
            style={{
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              color: '#cbd5e1', fontSize: 10, padding: '3px 8px', borderRadius: 999,
              cursor: 'pointer',
            }}
          >{qp}</button>
        ))}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); submit(q); }}
        style={{ display: 'flex', gap: 6 }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask Nova anything…"
          style={{
            flex: 1, background: '#020617', color: '#e2e8f0',
            border: '1px solid #1e293b', borderRadius: 8,
            padding: '8px 10px', fontSize: 12,
          }}
        />
        <button
          type="submit" disabled={busy || !q.trim()}
          style={{
            background: '#22d3ee', border: 'none', color: '#0f172a',
            padding: '8px 14px', borderRadius: 8, fontWeight: 800, fontSize: 12,
            cursor: busy ? 'wait' : 'pointer', opacity: busy || !q.trim() ? 0.5 : 1,
          }}
        >{busy ? '…' : 'Ask'}</button>
      </form>
    </div>
  );
}
